import { html, LitElement, nothing } from "lit";
import { customElement, property, state } from "lit/decorators.js";
import { Invitation } from "@/types";
import { formatDate } from "@/utils";
import {
  checkIcon,
  clockIcon,
  closeIcon,
  refreshCwIcon,
  searchIcon,
} from "@/assets/icons";

export type InvitationFilters = {
  roles: string[];
  status: string[];
};

type RoleOption = {
  value: string;
  label: string;
};

export type StatusOption = {
  value: string;
  label: string;
};

@customElement("sb-member-management-invitations-panel")
export class MemberManagementInvitationsPanel extends LitElement {
  @property({ attribute: false }) invitations: Invitation[] = [];
  @property({ type: Boolean, attribute: false }) invitationsLoading = false;
  @property({ type: String }) searchTerm = "";
  @property({ type: Object, attribute: false })
  invitationFilters: InvitationFilters = {
    roles: [],
    status: [],
  };
  @property({ type: Array, attribute: false }) availableRoles: RoleOption[] =
    [];
  @property({ type: Array, attribute: false }) statusOptions: StatusOption[] =
    [];
  @property({ type: Number }) invitationsPage = 1;
  @property({ type: Number }) invitationsTotalPages = 0;
  @property({ type: Number }) invitationsTotal = 0;
  @property({ type: Number }) rowsPerPage = 5;

  @state() private _searchDebounceTimer: number | undefined;

  protected createRenderRoot() {
    return this;
  }

  private dispatchInvitationsSearchChange(searchTerm: string) {
    this.dispatchEvent(
      new CustomEvent("invitations-search-change", {
        detail: { searchTerm },
        bubbles: true,
        composed: true,
      }),
    );
  }

  private handleInvitationsSearchChange(event: Event) {
    const input = event.target as HTMLInputElement;
    this.searchTerm = input.value;

    if (this._searchDebounceTimer) {
      window.clearTimeout(this._searchDebounceTimer);
    }

    this._searchDebounceTimer = window.setTimeout(() => {
      this._searchDebounceTimer = undefined;
      this.dispatchInvitationsSearchChange(this.searchTerm);
    }, 300);
  }

  private handleInvitationsPageChange(event: CustomEvent) {
    this.dispatchEvent(
      new CustomEvent("invitations-page-change", {
        detail: { page: event.detail.page },
        bubbles: true,
        composed: true,
      }),
    );
  }

  private handleRowsPerPageChange(event: Event) {
    const updatedValue = parseInt(
      (event.target as HTMLSelectElement).value,
      10,
    );
    this.dispatchEvent(
      new CustomEvent("invitations-rows-per-page-change", {
        detail: { rowsPerPage: updatedValue },
        bubbles: true,
        composed: true,
      }),
    );
  }

  private handleRefresh() {
    this.dispatchEvent(
      new CustomEvent("invitations-refresh", {
        bubbles: true,
        composed: true,
      }),
    );
  }

  private handleRolesChange(event: CustomEvent) {
    this.dispatchEvent(
      new CustomEvent("invitations-filter-change", {
        detail: {
          roles: event.detail.selected,
          status: this.invitationFilters.status,
        },
        bubbles: true,
        composed: true,
      }),
    );
  }

  private handleStatusChange(event: CustomEvent) {
    this.dispatchEvent(
      new CustomEvent("invitations-filter-change", {
        detail: { ...this.invitationFilters, status: event.detail.selected },
        bubbles: true,
        composed: true,
      }),
    );
  }

  private clearFilters() {
    this.dispatchEvent(
      new CustomEvent("invitations-clear-filters", {
        bubbles: true,
        composed: true,
      }),
    );
  }

  private removeFilter(filterValue: string) {
    this.dispatchEvent(
      new CustomEvent("invitations-remove-filter", {
        detail: { filterValue },
        bubbles: true,
        composed: true,
      }),
    );
  }

  private dispatchInvitationAction(action: string, invitation: Invitation) {
    this.dispatchEvent(
      new CustomEvent(action, {
        detail: invitation,
        bubbles: true,
        composed: true,
      }),
    );
  }

  private getSelectedFiltersDisplay() {
    const selectedFilters: Array<{
      name: string;
      value: string;
      label: string;
    }> = [];
    if (this.invitationFilters.roles?.length) {
      this.invitationFilters.roles.forEach((roleValue: string) => {
        const roleLabel =
          this.availableRoles.find((r) => r.value === roleValue)?.label ||
          roleValue;
        selectedFilters.push({
          name: "roles",
          value: roleValue,
          label: roleLabel,
        });
      });
    }
    if (this.invitationFilters.status?.length) {
      this.invitationFilters.status.forEach((statusValue: string) => {
        const statusLabel =
          this.statusOptions.find((s) => s.value === statusValue)?.label ||
          statusValue;
        selectedFilters.push({
          name: "status",
          value: statusValue,
          label: statusLabel,
        });
      });
    }
    return selectedFilters;
  }

  private getInvitationTableConfig(loading: boolean) {
    const cellRenderers = {
      invitee_email: (value: any) => {
        if (loading) {
          return html`
            <div class="sb-flex sb-items-center sb-gap-2">
              <rtg-skeleton
                class="sb-h-10 sb-w-10 sb-rounded-full"
              ></rtg-skeleton>
              <rtg-skeleton class="sb-h-4 sb-w-28"></rtg-skeleton>
            </div>
          `;
        }
        return html`
          <div class="sb-flex sb-items-center sb-gap-2">
            <rtg-avatar size="lg">
              <rtg-avatar-fallback>${searchIcon()}</rtg-avatar-fallback>
            </rtg-avatar>
            <rtg-typography-table part="invitee_email"
              >${value}</rtg-typography-table
            >
          </div>
        `;
      },
      roles: (value: { key: string; name: string }[]) => {
        if (loading) {
          return html`
            <div class="sb-flex sb-gap-2">
              <rtg-skeleton class="sb-h-5 sb-w-12 sb-rounded"></rtg-skeleton>
              <rtg-skeleton class="sb-h-5 sb-w-16 sb-rounded"></rtg-skeleton>
            </div>
          `;
        }
        return html`
          <div class="sb-flex sb-gap-1 sb-flex-wrap">
            ${value.map(
              (role) => html`
                <rtg-badge variant="outline">${role.name}</rtg-badge>
              `,
            )}
            ${value.length > 3
              ? html`<rtg-hover-card part="more-roles">
                  <rtg-hover-card-trigger>
                    <span class="sb-cursor-pointer">...</span>
                  </rtg-hover-card-trigger>
                  <rtg-hover-card-content>
                    ${value
                      .slice(3)
                      .map(
                        (role) => html`
                          <rtg-typography-small>${role}</rtg-typography-small>
                        `,
                      )}
                  </rtg-hover-card-content>
                </rtg-hover-card>`
              : nothing}
          </div>
        `;
      },
      invited_by: (value: any) => {
        if (loading) {
          return html` <rtg-skeleton class="sb-h-4 sb-w-20"></rtg-skeleton> `;
        }
        return html`
          <rtg-typography-table part="invited_by"
            >${value.name}</rtg-typography-table
          >
        `;
      },
      created_at: (value: any) => {
        if (loading) {
          return html`
            <div class="sb-flex sb-flex-col sb-gap-1">
              <rtg-skeleton class="sb-h-4 sb-w-16"></rtg-skeleton>
              <rtg-skeleton class="sb-h-3 sb-w-12"></rtg-skeleton>
            </div>
          `;
        }
        const { date, time } = formatDate(value);
        return html`
          <div class="sb-flex sb-flex-col">
            <rtg-typography-table part="created_at"
              >${date}</rtg-typography-table
            >
            <rtg-typography-muted part="created_time"
              >${time}</rtg-typography-muted
            >
          </div>
        `;
      },
      expires_at: (value: any) => {
        if (loading) {
          return html`
            <div class="sb-flex sb-flex-col sb-gap-1">
              <rtg-skeleton class="sb-h-4 sb-w-16"></rtg-skeleton>
              <rtg-skeleton class="sb-h-3 sb-w-12"></rtg-skeleton>
            </div>
          `;
        }
        const { date, time } = formatDate(value);
        return html`
          <div class="sb-flex sb-flex-col">
            <rtg-typography-table part="expires_at"
              >${date}</rtg-typography-table
            >
            <rtg-typography-muted part="expires_time"
              >${time}</rtg-typography-muted
            >
          </div>
        `;
      },
      status: (value: any) => {
        if (loading) {
          return html`
            <rtg-skeleton class="sb-h-5 sb-w-14 sb-rounded-full"></rtg-skeleton>
          `;
        }
        const icon =
          value === "pending"
            ? clockIcon()
            : value === "accepted"
              ? checkIcon()
              : nothing;
        return html`
          <div part=${`invitation-status`}>
            <rtg-badge variant="outline">
              <div part=${value + "-icon"}>${icon}</div>
              ${value}
            </rtg-badge>
          </div>
        `;
      },
    };

    const headerRenderers = Object.fromEntries(
      [
        "invitee_email",
        "roles",
        "invited_by",
        "created_at",
        "expires_at",
        "status",
      ].map((key) => [
        key,
        () => {
          const labels: Record<string, string> = {
            invitee_email: "Email",
            roles: "Roles",
            invited_by: "Invited By",
            created_at: "Invited At",
            expires_at: "Expires At",
            status: "Status",
          };
          return html` <span>${labels[key] || key}</span> `;
        },
      ]),
    );

    const rowActions = loading
      ? []
      : [
          {
            condition: (row: Invitation) =>
              row.status?.toLowerCase() === "pending" ||
              row.status?.toLowerCase() === "cancelled",
            render: () => html`<span>Resend invitation link</span>`,
            action: (row: Invitation) =>
              this.dispatchInvitationAction("resend-invitation", row),
          },
          {
            condition: (row: Invitation) =>
              row.status?.toLowerCase() === "pending",
            render: () =>
              html`<span part="danger-action">Cancel invitation link</span>`,
            action: (row: Invitation) =>
              this.dispatchInvitationAction("cancel-invitation", row),
          },
        ];

    return {
      cellRenderers,
      headerRenderers,
      rowActions,
      sortableColumns: loading
        ? []
        : ["invitee_email", "created_at", "expires_at", "status"],
    };
  }

  private renderPageLengthSelection() {
    const startIndex =
      this.invitationsTotal === 0
        ? 0
        : (this.invitationsPage - 1) * this.rowsPerPage + 1;
    const endIndex = Math.min(
      this.invitationsPage * this.rowsPerPage,
      this.invitationsTotal,
    );

    return html`
      <div class="sb-flex sb-items-center sb-gap-1">
        <rtg-typography-muted>Rows per page</rtg-typography-muted>
        <rtg-select
          .value=${`${this.rowsPerPage}`}
          @change=${this.handleRowsPerPageChange}
        >
          <rtg-select-trigger data-testid="trigger" class="sb-w-full">
            <rtg-select-value></rtg-select-value>
          </rtg-select-trigger>
          <rtg-select-group slot="content">
            <rtg-select-item tabindex="0" value="5">5</rtg-select-item>
            <rtg-select-item tabindex="1" value="10">10</rtg-select-item>
            <rtg-select-item tabindex="2" value="25">25</rtg-select-item>
          </rtg-select-group>
        </rtg-select>
        <div class="sb-ml-1"></div>
        <rtg-separator orientation="vertical"></rtg-separator>
        <div class="sb-mr-1"></div>
        <rtg-typography-muted>
          ${this.invitationsTotal === 0
            ? "Showing 0 items"
            : `Showing ${startIndex} to ${endIndex} items of ${this.invitationsTotal}`}
        </rtg-typography-muted>
      </div>
    `;
  }

  render() {
    const invitationConfig = this.getInvitationTableConfig(
      this.invitationsLoading,
    );
    const invitationsStub = Array.from(
      { length: this.rowsPerPage },
      (_, i) => ({
        id: `stub-invitation-${i}`,
        invitee_email: "",
        roles: [],
        invited_by: { id: "", name: "" },
        created_at: 0,
        expires_at: 0,
        status: "",
      }),
    );

    const invitationsData = this.invitationsLoading
      ? invitationsStub
      : this.invitations;

    return html`
      <div class="sb-flex sb-justify-between">
        <div class="sb-mb-4 sb-flex sb-flex-wrap sb-items-center sb-gap-4">
          <rtg-input-group class="sb-flex-shrink-0 !sb-w-auto">
            <rtg-input-group-input
              placeholder="Search invitations..."
              .value=${this.searchTerm}
              @input=${this.handleInvitationsSearchChange}
            ></rtg-input-group-input>
            <rtg-input-group-addon>${searchIcon()}</rtg-input-group-addon>
          </rtg-input-group>
          <div class="sb-flex sb-items-center sb-gap-2">
            <sb-multi-select-filter
              label="Roles"
              placeholder="All roles"
              .options=${this.availableRoles}
              .selected=${this.invitationFilters.roles}
              @change=${this.handleRolesChange}
            ></sb-multi-select-filter>
            <sb-multi-select-filter
              label="Status"
              placeholder="All statuses"
              .options=${this.statusOptions}
              .selected=${this.invitationFilters.status}
              @change=${this.handleStatusChange}
            ></sb-multi-select-filter>
            ${this.invitationFilters.roles?.length ||
            this.invitationFilters.status?.length
              ? html`
                  <rtg-button
                    @click=${this.clearFilters}
                    variant="ghost"
                    size="sm"
                  >
                    ${closeIcon()} Clear All filters
                  </rtg-button>
                `
              : nothing}
          </div>
        </div>
        <rtg-button @click=${this.handleRefresh} variant="outline" size="icon">
          ${refreshCwIcon()}
        </rtg-button>
      </div>

      ${this.getSelectedFiltersDisplay().length
        ? html`
            <div class="sb-flex sb-flex-wrap sb-gap-2 sb-mb-4">
              ${this.getSelectedFiltersDisplay().map(
                (filter) => html`
                  <rtg-badge variant="secondary">
                    ${filter.label}
                    <span
                      class="sb-cursor-pointer sb-ml-1"
                      @click=${() => this.removeFilter(filter.value)}
                    >
                      ${closeIcon()}
                    </span>
                  </rtg-badge>
                `,
              )}
            </div>
          `
        : nothing}

      <rtg-data-table
        data-tab="invitations"
        .dataTable=${invitationsData}
        .cellRenderers=${invitationConfig.cellRenderers}
        .headerRenderers=${invitationConfig.headerRenderers}
        .rowActions=${invitationConfig.rowActions}
        .pageSize=${invitationsData.length}
        .hasDefaultPagination=${false}
        .hasDefaultSearch=${false}
        .hasDefaultSelect=${false}
        .sortableColumns=${invitationConfig.sortableColumns}
        .columnOrder=${[
          "invitee_email",
          "roles",
          "invited_by",
          "created_at",
          "expires_at",
          "status",
        ]}
      ></rtg-data-table>

      ${!this.invitationsLoading
        ? html`
            <div class="sb-mt-4 sb-flex sb-justify-between">
              ${this.renderPageLengthSelection()}
              <div>
                <sb-pagination
                  .totalPages=${this.invitationsTotalPages}
                  .currentPage=${this.invitationsPage}
                  @page-change=${this.handleInvitationsPageChange}
                ></sb-pagination>
              </div>
            </div>
          `
        : nothing}
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    "sb-member-management-invitations-panel": MemberManagementInvitationsPanel;
  }
}
